import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';

const formats = [
  {
    num: '01',
    label: 'Format One',
    title: 'Group Workshops & Classes',
    body: 'Daily or alternating-day sessions held in a communal lounge or library, open to all passengers. Sessions are carefully pitched by level — so a complete beginner and an experienced club player can both find something genuinely worthwhile on the same voyage. Interactive, entertaining, and fully accessible — no prior knowledge assumed for beginners’ classes.',
    badges: ['Beginners’ Introduction', 'Intermediate Workshop', 'Advanced Masterclass'],
    details: [
      ['Duration', '60–75 minutes per session'],
      ['Capacity', 'Up to 30–40 passengers'],
      ['Frequency', 'Daily or alternating-day'],
      ['Prior knowledge', 'None required for beginners'],
    ],
  },
  {
    num: '02',
    label: 'Format Two',
    title: 'One-to-One Coaching',
    body: 'Private or small-group coaching sessions available for passengers seeking personalised instruction. Bookable through the activities desk in 45–60 minute slots. Tuition is tailored entirely to the individual — whether they are picking up the game for the first time, returning to it after years away, or an experienced player looking to improve a specific aspect of their game. This is the format that creates the most memorable passenger experiences.',
    details: [
      ['Duration', '45–60 minutes per session'],
      ['Format', 'One-to-one or small group'],
      ['Booking', 'Via activities desk'],
      ['Pricing', 'Additional fee basis if charged to passengers'],
    ],
  },
  {
    num: '03',
    label: 'Format Three',
    title: 'The Onboard Tournament',
    body: 'A social ship’s tournament — run over two to three days with a final session on the penultimate or last sea day — provides a focal point and a genuine sense of occasion. The tournament is structured to be inclusive rather than competitive: separate brackets for beginners and experienced players, with emphasis on fun and social connection. A brief prize-giving creates a natural moment of public recognition — and a talking point that carries beyond the voyage.',
    badges: ['Open Bracket', 'Beginners’ Bracket', 'Prize Giving', 'Certificates'],
    details: [
      ['Duration', '2–3 days with final session'],
      ['Structure', 'Swiss system, all levels'],
      ['Prize giving', 'Public ceremony, suitable for photography'],
      ['Theme', 'Can be themed to voyage destination'],
    ],
  },
];

const takeHome = [
  ['♟', 'A New Game', 'Complete beginners leave the voyage knowing how to play chess — a skill they can share with family and friends for the rest of their lives.'],
  ['♞', 'Expert Coaching', 'Personalised instruction from a qualified coach who has played at the highest level — tailored to the individual’s goals and current ability.'],
  ['♜', 'Technology Access', 'Hands-on time with the SenseRobot AI chess system — something most passengers will never have encountered before, and will never forget.'],
  ['♛', 'Tournament Experience', 'The experience of competing in a formal ship’s tournament — with the camaraderie, the tension, and the satisfaction of a prize-giving ceremony.'],
  ['♚', 'Social Connection', 'Chess connects passengers across age groups and backgrounds in a way few other activities can — a screen-free, face-to-face, genuinely shared experience.'],
  ['♝', 'Independent Play', 'Auto-playing electronic boards available throughout the voyage — so the chess experience continues between sessions, at any time of day.'],
];

const fees = [
  ['Short voyage (3–5 nights)', '£300–£400', 'Reduced programme scope'],
  ['Medium voyage (7–10 nights)', '£500–£700', 'Full programme — all three formats'],
  ['Long voyage (14+ nights)', '£800–£1,200', 'Extended programme, multiple tournaments'],
  ['Consecutive voyages', 'Negotiable', 'Adjusted terms available for a series of bookings'],
  ['Pilot/trial voyage', 'Reduced rate', 'Available to demonstrate programme before committing'],
];

export default function Programme() {
  return (
    <>
      <PageHero
        tag="The Programme"
        title={<>Chess that passengers <em>remember</em></>}
        lede="A fully structured enrichment residency — group workshops, private coaching, and a social tournament — delivered with all equipment supplied and zero overhead to the cruise line."
      />

      <section className="formats">
        <div className="inner">
          <Reveal>
            <p className="section-tag">Three Formats</p>
            <h2 className="section-title">A complete <em>chess experience</em></h2>
            <div className="divider" />
            <p className="section-body">
              Chess at Sea is structured around three complementary formats, each designed to appeal
              to a different type of passenger and to maximise participation across the full voyage.
            </p>
          </Reveal>

          {formats.map((f) => (
            <Reveal key={f.num} className="format-block">
              <div>
                <div className="format-num">{f.num}</div>
                <div className="format-label">{f.label}</div>
              </div>
              <div>
                <h2 className="format-title">{f.title}</h2>
                <p className="format-body">{f.body}</p>
                {f.badges && (
                  <div className="level-badges">
                    {f.badges.map((b) => <span className="badge" key={b}>{b}</span>)}
                  </div>
                )}
                <div className="format-details">
                  {f.details.map(([k, v]) => (
                    <div className="detail-item" key={k}>
                      <strong>{k}</strong><span>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="what-you-get on-dark">
        <div className="inner">
          <Reveal>
            <p className="section-tag">Passenger Value</p>
            <h2 className="section-title">What passengers <em>take home</em></h2>
            <div className="divider" />
            <p className="section-body">
              Chess at Sea offers something genuinely rare in cruise enrichment: the opportunity to
              acquire a lasting skill. Unlike a lecture, chess tuition delivers something passengers
              keep long after the voyage ends.
            </p>
          </Reveal>
          <div className="get-grid">
            {takeHome.map(([icon, title, body], i) => (
              <Reveal key={title} className="get-card" delay={(i % 3) * 90}>
                <div className="get-icon">{icon}</div>
                <h3>{title}</h3>
                <p>{body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="requirements">
        <div className="inner">
          <Reveal>
            <p className="section-tag">Practicalities</p>
            <h2 className="section-title">Minimal footprint. <em>Maximum value.</em></h2>
            <div className="divider" />
            <p className="section-body">
              Chess at Sea has been designed to integrate seamlessly into the existing onboard
              schedule with the smallest possible demand on the cruise line&rsquo;s resources.
            </p>
          </Reveal>
          <div className="req-grid">
            <Reveal className="req-col">
              <h3>What the line provides</h3>
              <ul className="req-list">
                <li>A lounge, card room, or library space seating 20–40 passengers</li>
                <li>Inclusion in the daily programme and onboard communications</li>
                <li>A brief mention in pre-departure passenger communications</li>
                <li>Access to an activities staff liaison for scheduling and bookings</li>
                <li>Cabin (inside or oceanview, sole occupancy) and full board</li>
              </ul>
            </Reveal>
            <Reveal className="req-col" delay={120}>
              <h3>What the line does NOT need to provide</h3>
              <ul className="req-list">
                <li className="cross">Any chess equipment whatsoever</li>
                <li className="cross">Audio-visual or technical staging</li>
                <li className="cross">Specialist venue or dedicated space</li>
                <li className="cross">Staff support during sessions</li>
                <li className="cross">Setup or breakdown assistance</li>
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="commercial">
        <div className="inner">
          <Reveal>
            <p className="section-tag">Commercial Terms</p>
            <h2 className="section-title">Transparent, <em>fair pricing</em></h2>
            <div className="divider" />
            <p className="section-body">
              The arrangement follows the standard hybrid model for enrichment specialists in the
              cruise industry — cabin and board provided by the line, with a per-voyage instructor fee.
            </p>
          </Reveal>
          <Reveal delay={100}>
            <table className="fee-table">
              <thead>
                <tr><th>Voyage Length</th><th>Suggested Fee Range</th><th>Notes</th></tr>
              </thead>
              <tbody>
                {fees.map(([a, b, c]) => (
                  <tr key={a}><td>{a}</td><td>{b}</td><td>{c}</td></tr>
                ))}
              </tbody>
            </table>
            <div className="pullquote" style={{ marginTop: 32 }}>
              <p>“A credentialed professional enrichment programme, with technology that creates genuine passenger excitement, at a very modest cost relative to headline guest speakers or specialist entertainers.”</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="cta-band">
        <Reveal>
          <h2>Ready to bring Chess <em>at Sea</em> aboard?</h2>
          <p>I would welcome a conversation about how this programme can work for your fleet and passenger profile.</p>
          <Link to="/about#contact" className="btn-primary">Get in Touch <span className="arrow">→</span></Link>
        </Reveal>
      </section>
    </>
  );
}
